import type { EditorManifest as LegacyManifest, EditorManifestV2 } from "@prototypical/editor-contract";

/** Browser-safe projection; the compiler package contains server-only dependencies. */
export type EditorManifestV3 = Omit<EditorManifestV2, "schemaVersion" | "styles" | "fields"> & {
  schemaVersion: 3;
  fields: Array<EditorManifestV2["fields"][number] & {
    metadata?: { assetId?: string; alt?: string; focalX?: number; focalY?: number };
    source: { moduleId: string; editId: string; attribute: "text" | "href" | "src" | "alt"; binding?: string };
  }>;
  styles: {
    tokens: Array<{ name: string; cssVariable: string; value: string; kind: "color" | "font" | "dimension" | "other" }>;
    fonts: Array<{ id: string; family: string; cssFamily: string; role?: string; stylesheetUrl?: string }>;
    capabilities: { colors: string[]; fonts: boolean };
  };
  source: { schemaVersion: 1; modules: Array<{ id: string; kind: "shell" | "page" | "shared"; dependencies: string[]; exports?: string[] }> };
};

export type EditorManifest = LegacyManifest | EditorManifestV3;

export type SiteSourceOperation =
  | { op: "set-token"; name: string; value: string }
  | { op: "set-font"; id: string; family: string; cssFamily: string; stylesheetUrl?: string }
  | { op: "set-field"; path: string; value: string; capability?: "text" | "href" | "src" | "alt" }
  | { op: "set-image"; path: string; assetId: string; alt?: string; focalX?: number; focalY?: number }
  | { op: "set-page-metadata"; page: string; title?: string; description?: string };

export function authoredStyles(manifest: EditorManifest | null): Record<string, string> {
  if (manifest?.schemaVersion === 3) return Object.fromEntries([
    ...manifest.styles.tokens.map(token => [token.name, token.value]),
    ...manifest.styles.fonts.map(font => [`font.${font.id}`, font.cssFamily]),
  ]);
  return manifest?.schemaVersion === 2
    ? Object.fromEntries((manifest.styles?.tokens ?? []).flatMap(token => token.value ? [[token.name, token.value]] : []))
    : manifest?.colors ?? {};
}
